import { useState } from 'react';
import { useDebouncedValue } from '@/hooks/use-debounced-value';

interface UseResettableDebouncedValueResult {
  /** Immediate value — bind this to the input. */
  value: string;
  setValue: (v: string) => void;
  /** Debounced value — pass this to the search/preview query. */
  debounced: string;
  /** Instantly resets both `value` and `debounced` to empty — a plain `setValue('')` would
   *  still take `delayMs` to reach `debounced`. */
  reset: () => void;
}

/** Shared primitive behind `useTableSearch` and `useDebouncedUserSearch` — both need
 *  "debounce this text input, but let the debounced side snap back to empty instantly on
 *  an explicit reset" (clearing a table search, closing a selected-user dropdown). */
export function useResettableDebouncedValue(delayMs: number): UseResettableDebouncedValueResult {
  const [value, setValueRaw] = useState('');
  // useDebouncedValue always waits the full delay, even to reflect an empty string —
  // `cleared` is the instant-reset escape hatch.
  const [cleared, setCleared] = useState(false);
  const debouncedRaw = useDebouncedValue(value, delayMs);
  const debounced = cleared ? '' : debouncedRaw;

  function setValue(v: string) {
    setCleared(false);
    setValueRaw(v);
  }

  function reset() {
    setValueRaw('');
    setCleared(true);
  }

  return { value, setValue, debounced, reset };
}
